//@flow
import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {Card, CardActionArea, Typography} from '@material-ui/core';
import {AddCircleTwoTone} from '@material-ui/icons';

import {colors} from '../../../../../assets/styles/colors';

import {commaFormat} from '../../../../../utils/number';
import type {Tax, Store, Currency} from '../../../../../entities';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {useMemo} from 'react';

type Props = {|
  tax?: Tax,
  type?: 'CREATE',
  disabledCreate?: boolean,
  handleClick?: () => void,
  currency?: Currency,
  store?: Store,
  resetState?: () => void,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    position: 'relative',
    height: '100%',
    width: '100%',
    margin: '0 auto',
    padding: '16px',
    background: '#FCFCFD',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '8px',
  },
  cardRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    position: 'relative',
    height: '100%',
    width: '100%',
    boxShadow: 'unset',
    margin: '0 auto',
  },
  cardActionArea: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: '100%',
    borderRadius: '12px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 216,
    height: '100%',
    width: '100%',
    border: '2px dashed #B8B8C7',
    opacity: 0.6,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 20,
    borderRadius: '12px',
    '&:hover': {
      opacity: 1,
    },
    transition: 'opacity 0.2s',
  },
  titleWrap: {
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '22px',
    color: colors.grey800,
    margin: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  paragraph: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '22px',
    color: colors.grey700,
    margin: '0',
    maxWidth: '200px',
    textAlign: 'center',
  },
  chip: {
    background: '#00B382',
    borderRadius: '12px',
    color: 'white',
    padding: '2px 8px',
    height: 24,
  },
  chipExpired: {
    background: '#E04763',
  },
  icon: {
    background: '#EEE5FF',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    marginRight: '4px',
    padding: '4px',
  },
  description: {
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.005em',
    color: colors.grey800,
    marginBottom: '8px',
  },
  buttonLink: {
    width: '100%',
    padding: 0,
    borderRadius: 8,
    height: '100%',
    '& > span': {
      height: '100%',
    },
  },
}));

export default function TaxCard(props: Props) {
  const {
    tax,
    type = '',
    handleClick,
    currency,
    resetState,
    disabledCreate = false,
  } = props;

  const classes = useStyles();

  const listCategoriesName = useMemo(() => {
    if (tax && tax.taxCriteria === 'PRODUCT_CATEGORY') {
      if (tax.taxProductCategories && tax.taxProductCategories.length) {
        let name = `Categoria${
          tax.taxProductCategories.length > 1 ? 's' : ''
        } - `;

        const arrNames = tax.taxProductCategories
          .map(({productCategory}) => productCategory.name)
          .join(',');
        return name + arrNames;
      }
      return 'Categorias';
    }
    return '';
  }, [tax]);

  return type === 'CREATE' ? (
    <Card classes={{root: classes.cardRoot}}>
      {handleClick && (
        <CardActionArea
          className={classes.cardActionArea}
          onClick={() => handleClick()}
          disabled={disabledCreate}
        >
          <div className={classes.empty}>
            <AddCircleTwoTone color="primary" fontSize="large" />
            <Typography className={classes.paragraph}>
              Agregar configuración de impuesto.
            </Typography>
          </div>
        </CardActionArea>
      )}
    </Card>
  ) : tax && currency ? (
    <CustomButton
      to={`/configure/taxes/${tax.id}`}
      onClick={resetState}
      className={classes.buttonLink}
    >
      <Card classes={{root: classes.root}}>
        <div className={classes.titleWrap}>
          <div style={{wordBreak: 'break-word', maxWidth: '70%'}}>
            <Typography className={classes.title} paragraph>
              {tax.name}
            </Typography>
          </div>

          <span
            className={`${classes.chip} ${
              tax.enabled ? '' : classes.chipExpired
            }`}
          >
            {tax.enabled ? 'Activo' : 'Inactivo'}
          </span>
        </div>
        <div style={{height: '100%'}}>
          <ul>
            {tax.percentage && (
              <li className={classes.description}>
                {parseFloat(Number(tax.percentage) * 100).toFixed(0)} %
              </li>
            )}
            <li className={classes.description}>{`Asignar por: ${
              ['PRODUCT', 'ORDER', 'PRODUCT_CATEGORY'].includes(tax.taxCriteria)
                ? 'Producto'
                : 'Monto'
            }`}</li>
            {tax.taxCriteria && (
              <li className={classes.description}>{`Aplica a: ${
                tax.taxCriteria === 'ORDER'
                  ? 'Toda la orden'
                  : tax.taxCriteria === 'PRODUCT'
                  ? 'Productos específicos'
                  : listCategoriesName
              }`}</li>
            )}
            {tax.taxCriteria && tax.minOrderAmount && currency && (
              <li className={classes.description}>{`Mayores a: ${commaFormat(
                tax.minOrderAmount,
                currency.symbol
              )}`}</li>
            )}
          </ul>
        </div>
      </Card>
    </CustomButton>
  ) : null;
}

TaxCard.propTypes = {
  tax: PropTypes.object,
  type: PropTypes.string,
  handleClick: PropTypes.func,
  currency: PropTypes.object,
  store: PropTypes.object,
  resetState: PropTypes.func.isRequired,
};
